package com.example.event_management_system.ui.components.calendar

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.example.event_management_system.ui.theme.Dimens
import com.example.event_management_system.util.currentLocalDate
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus

@Composable
fun CalendarYearView(
    year: Int,
    currentMonth: LocalDate,
    onMonthClick: (LocalDate) -> Unit,
    modifier: Modifier = Modifier
) {
    val today = currentLocalDate()
    val months = (1..12).map { Month(it) }

    LazyVerticalGrid(
        columns = GridCells.Fixed(4),
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(Dimens.paddingMedium),
        horizontalArrangement = Arrangement.spacedBy(Dimens.spacingLarge),
        verticalArrangement = Arrangement.spacedBy(Dimens.spacingXLarge)
    ) {
        items(months) { month ->
            val monthDate = LocalDate(year, month, 1)
            MiniMonthBlock(
                monthDate = monthDate,
                today = today,
                onClick = { onMonthClick(monthDate) }
            )
        }
    }
}

@Composable
private fun MiniMonthBlock(
    monthDate: LocalDate,
    today: LocalDate,
    onClick: () -> Unit
) {
    val monthName = monthDate.month.name.lowercase().replaceFirstChar { it.uppercase() }
    val daysOfWeek = listOf("S", "M", "T", "W", "T", "F", "S")
    val allDates = buildMiniCalendarDates(monthDate)
    val weeks = allDates.chunked(7)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Text(
            text = monthName,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary,
            modifier = Modifier.padding(bottom = Dimens.spacingXSmall)
        )

        Row(modifier = Modifier.fillMaxWidth()) {
            daysOfWeek.forEach { day ->
                Text(
                    text = day,
                    modifier = Modifier.weight(1f),
                    textAlign = TextAlign.Center,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }

        weeks.forEach { week ->
            Row(modifier = Modifier.fillMaxWidth()) {
                week.forEach { (date, isInMonth) ->
                    val isToday = date == today
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .aspectRatio(1f),
                        contentAlignment = Alignment.Center
                    ) {
                        if (isToday) {
                            Box(
                                modifier = Modifier
                                    .size(Dimens.calendarYearDaySize)
                                    .clip(CircleShape)
                                    .background(MaterialTheme.colorScheme.primary),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = date.dayOfMonth.toString(),
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.Bold,
                                    textAlign = TextAlign.Center,
                                    color = MaterialTheme.colorScheme.onPrimary
                                )
                            }
                        } else {
                            Text(
                                text = date.dayOfMonth.toString(),
                                style = MaterialTheme.typography.labelSmall,
                                textAlign = TextAlign.Center,
                                color = if (isInMonth) MaterialTheme.colorScheme.onSurface
                                        else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f)
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun buildMiniCalendarDates(currentMonth: LocalDate): List<Pair<LocalDate, Boolean>> {
    val firstDay = LocalDate(currentMonth.year, currentMonth.month, 1)
    val startOffset = (firstDay.dayOfWeek.ordinal + 1) % 7
    val daysInMonth = when (currentMonth.month) {
        Month.JANUARY, Month.MARCH, Month.MAY, Month.JULY,
        Month.AUGUST, Month.OCTOBER, Month.DECEMBER -> 31
        Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
        Month.FEBRUARY -> if ((currentMonth.year % 4 == 0 && currentMonth.year % 100 != 0)
                              || currentMonth.year % 400 == 0) 29 else 28
    }

    val prevMonth = currentMonth.plus(-1, DateTimeUnit.MONTH)
    val daysInPrevMonth = when (prevMonth.month) {
        Month.JANUARY, Month.MARCH, Month.MAY, Month.JULY,
        Month.AUGUST, Month.OCTOBER, Month.DECEMBER -> 31
        Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
        Month.FEBRUARY -> if ((prevMonth.year % 4 == 0 && prevMonth.year % 100 != 0)
                              || prevMonth.year % 400 == 0) 29 else 28
    }
    val nextMonth = currentMonth.plus(1, DateTimeUnit.MONTH)

    val list = mutableListOf<Pair<LocalDate, Boolean>>()

    for (i in startOffset - 1 downTo 0) {
        val day = daysInPrevMonth - i
        list.add(LocalDate(prevMonth.year, prevMonth.month, day) to false)
    }
    for (day in 1..daysInMonth) {
        list.add(LocalDate(currentMonth.year, currentMonth.month, day) to true)
    }
    var nextDay = 1
    var nm = nextMonth
    while (list.size < 42) {
        if (nextDay > daysInMonthOf(nm)) {
            nm = nm.plus(1, DateTimeUnit.MONTH)
            nextDay = 1
        }
        list.add(LocalDate(nm.year, nm.month, nextDay++) to false)
    }
    return list
}

private fun daysInMonthOf(month: LocalDate): Int = when (month.month) {
    Month.JANUARY, Month.MARCH, Month.MAY, Month.JULY,
    Month.AUGUST, Month.OCTOBER, Month.DECEMBER -> 31
    Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
    Month.FEBRUARY -> if ((month.year % 4 == 0 && month.year % 100 != 0)
                          || month.year % 400 == 0) 29 else 28
}
